/* Every string the About and Contact pages put on screen. The heart section
 * keeps its own copy in `@/components/sections/amour/data`; this file is the
 * rest of the studio, so a buyer edits two files and ships. */

export interface NavLink {
    href: string
    label: string
}

export const NAV: NavLink[] = [
    { href: '/', label: 'Archive' },
    { href: '/about', label: 'About' },
    { href: '/contact', label: 'Contact' }
]

export const STUDIO_INFO = {
    mark: 'AMOUR',
    line: 'Portrait studio, Lisbon',
    address: ['Rua da Boavista 84', '1200-068 Lisbon, Portugal'],
    email: 'hello@amour.studio',
    phone: '+351 21 040 8814',
    hours: 'Tuesday to Saturday, 10:00 – 19:00',
    social: [
        { label: 'Instagram', href: 'https://instagram.com' },
        { label: 'Vimeo', href: 'https://vimeo.com' },
        { label: 'Journal', href: '#!' }
    ]
}

/* ── About ─────────────────────────────────────────────────────────────── */

export const ABOUT = {
    label: 'About the studio',
    headSmall: 'One room, one gel,',
    headLarge: 'and whoever walks in',
    lede: 'Amour has been photographing people against a single sheet of seamless for ten years. The only thing that changes between sittings is the colour we point at the wall.',
    story: {
        label: 'The method',
        headSmall: 'Colour does',
        headLarge: 'the describing',
        body: [
            'Every frame in the archive was lit the same way. One head, one gel, one figure held mostly in shadow. At the size a portrait is usually looked at, that leaves you a colour and an outline, and we have spent a decade finding out how much those two can carry.',
            'It is a deliberately narrow set of rules, and staying inside them is the whole discipline. Ten years of it means the archive reads as one body of work rather than four hundred separate afternoons, which is the reason the frames on the front page can be made to assemble into a single shape at all.',
            'We shoot for magazines, record labels, dance companies, and for people who have never once been photographed properly and have decided this is the year.'
        ]
    },
    stats: [
        { figure: '10', label: 'Years in the room' },
        { figure: '40', label: 'Frames in the public archive' },
        { figure: '06', label: 'Gels on the shelf' },
        { figure: '01', label: 'Sheet of seamless' }
    ],
    process: {
        label: 'How a sitting runs',
        headSmall: 'Four steps,',
        headLarge: 'nothing improvised',
        steps: [
            {
                n: '01',
                title: 'The conversation',
                body: 'Twenty minutes, usually on the phone. We ask what the picture is for and who it has to convince, and that answer decides everything after it.'
            },
            {
                n: '02',
                title: 'The colour',
                body: 'We choose one gel and commit to it. There is no second setup and no safety option waiting in the corner, which is exactly the point.'
            },
            {
                n: '03',
                title: 'The sitting',
                body: 'Ninety minutes, no crowd, no monitor to crowd around. Most sitters stop performing somewhere around minute forty, and that is where the frame comes from.'
            },
            {
                n: '04',
                title: 'The edit',
                body: 'You see every exposure. We select twelve, you keep three, and the negatives stay in the archive under your name for as long as you want them there.'
            }
        ]
    },
    people: {
        label: 'In the room',
        members: [
            {
                name: 'Lucía Amorim',
                role: 'Photographer, founder',
                note: 'Opened the studio in 2016 after eight years assisting on fashion sets she describes as far too crowded.'
            },
            {
                name: 'Tomás Reis',
                role: 'Lighting and printing',
                note: 'Builds every setup and hand-prints the archive. Owns all six gels and will not be parted from the amber.'
            }
        ]
    },
    credits: {
        label: 'Selected credits',
        items: [
            'Aperture',
            'Le Monde M',
            'Dazed',
            'Fantastic Man',
            'Nowness',
            'Kunsthal Rotterdam',
            'National Ballet of Portugal',
            'Verve Records'
        ]
    },
    cta: {
        headSmall: 'The room is warm,',
        headLarge: 'the wall is empty',
        body: 'Sittings are booked four to six weeks out. Tell us what the picture is for and we will send dates.',
        action: 'Book a sitting'
    }
}

/* ── Contact ───────────────────────────────────────────────────────────── */

export const CONTACT = {
    label: 'Contact',
    headSmall: 'Tell us what',
    headLarge: 'the picture is for',
    lede: 'One message is enough to start. If it turns into a sitting, we send dates and a colour before anything else.',
    promise: 'We answer every message within two working days.',
    formLabel: 'Send a message',
    subjects: [
        'Portrait sitting',
        'Editorial commission',
        'Campaign',
        'Prints and archive',
        'Something else'
    ],
    budgets: [
        'Under €2,000',
        '€2,000 – €5,000',
        '€5,000 – €15,000',
        '€15,000 and up',
        'Not sure yet'
    ],
    success: {
        title: 'Message received',
        body: 'It is in the studio book now. Expect a reply within two working days, from a real person who has read the whole thing.',
        again: 'Send another'
    }
}
